import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import faqHero from "@/assets/questions-hero.jpg";

const FAQ = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>FAQ | Rick's Black Car Service San Antonio</title>
        <meta name="description" content="Frequently asked questions about Rick's Black Car Service in San Antonio. Booking, pricing, services, and more. Call (210) 296-7409" />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/faq" />
        
        {/* Open Graph */}
        <meta property="og:title" content="FAQ | Rick's Black Car Service San Antonio" />
        <meta property="og:description" content="Frequently asked questions about Rick's Black Car Service in San Antonio. Booking, pricing, services, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ricksblackcarservicesantonio.com/faq" />
        
        {/* FAQ Schema Markup - Complete */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does black car service cost in San Antonio?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our rates start at $75 for airport transfers. Pricing varies based on distance, duration, and service type. Contact us at (210) 296-7409 for a custom quote."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between black car service and Uber/Lyft?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Black car service offers professional chauffeurs, luxury vehicles, guaranteed availability, flat-rate pricing (no surge), flight tracking, and a premium experience. Perfect for business travelers and special occasions where reliability and professionalism matter."
                }
              },
              {
                "@type": "Question",
                "name": "Do you service San Antonio International Airport (SAT)?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Airport transfers are one of our most popular services. We track your flight and adjust pickup times for delays. Meet and greet service available."
                }
              },
              {
                "@type": "Question",
                "name": "Are you available 24/7?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Rick's Black Car Service operates 24 hours a day, 7 days a week, including holidays."
                }
              },
              {
                "@type": "Question",
                "name": "What type of vehicle do you use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We operate a pristine 2025 Audi Q7 with Black Optic & Executive Package - one of Europe's most luxurious SUVs, featuring premium leather, advanced safety features, and spacious comfort."
                }
              },
              {
                "@type": "Question",
                "name": "How far in advance should I book?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We recommend booking at least 24 hours in advance for guaranteed availability, but we often accommodate same-day requests."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer hourly rates?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we offer hourly chauffeur service for wine tours, multiple stops, or extended transportation needs."
                }
              },
              {
                "@type": "Question",
                "name": "Is gratuity included?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Gratuity is not included but always appreciated for exceptional service."
                }
              },
              {
                "@type": "Question",
                "name": "What is your cancellation policy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Point-to-point transfers: Free cancellation up to 1 hour before pickup. Hourly/Charter: Free cancellation up to 24 hours before scheduled start. Late cancellations and no-shows: Full fare applies."
                }
              },
              {
                "@type": "Question",
                "name": "Do you serve areas outside San Antonio?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we serve Boerne, New Braunfels, San Marcos, and can arrange transportation to Austin and other Texas cities."
                }
              },
              {
                "@type": "Question",
                "name": "How does airport pickup at SAT work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We track your flight and time your pickup to your actual landing. Meeting points at Terminal A or B curbside at designated livery zones, or meet & greet at baggage claim upon request. Your chauffeur will text you upon arrival. Grace period: 30 minutes for domestic arrivals, 60 minutes for international."
                }
              },
              {
                "@type": "Question",
                "name": "What wait time is included?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "30 minutes included for domestic arrivals; 60 minutes for international. Excess time billed in 30-minute increments at the hourly rate."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer meet & greet service and what does it cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, chauffeur meets you at baggage claim exit and provides vehicle make, model, color and license plate number. No additional fee."
                }
              },
              {
                "@type": "Question",
                "name": "How do you handle delays and flight changes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We adjust to airline delays automatically through flight tracking. If your pickup time changes significantly, we'll confirm the new time and vehicle availability."
                }
              },
              {
                "@type": "Question",
                "name": "Do you provide car seats for children?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Rick's Black Car Service does NOT provide car seats or booster seats. Customers traveling with children must bring their own car seats that comply with Texas state law. Using car seats significantly reduces available seating capacity in our Audi Q7. Please inform us in advance when booking."
                }
              },
              {
                "@type": "Question",
                "name": "Can I bring a pet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Small pets in carriers are welcome. Cleaning fees may apply. Service animals are always accommodated."
                }
              },
              {
                "@type": "Question",
                "name": "Is smoking allowed?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "All vehicles are strictly non-smoking to maintain a clean, elegant cabin."
                }
              },
              {
                "@type": "Question",
                "name": "What payment methods do you accept?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Major credit cards and digital invoices. E-mailed receipts provided immediately after the trip."
                }
              },
              {
                "@type": "Question",
                "name": "What's the difference between hourly service and point-to-point?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Point-to-point: A-to-B transfers with fixed routing and pricing. Hourly: Continuous service with your chauffeur on standby for multiple stops or events."
                }
              },
              {
                "@type": "Question",
                "name": "What areas do you serve around San Antonio?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Greater San Antonio including SAT airport, downtown, Pearl District, River Walk, Alamo Heights, Stone Oak, Dominion, La Cantera, New Braunfels, Boerne, Canyon Lake, Fair Oaks Ranch, Lakehills and Seguin."
                }
              },
              {
                "@type": "Question",
                "name": "Do you handle weddings, proms, and special events?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we provide tailored schedules, staging plans, and VIP arrival coordination. We work with planners and venues to ensure seamless timelines."
                }
              },
              {
                "@type": "Question",
                "name": "How do I update or confirm my booking?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Reply to your confirmation message, direct text, or contact us with your changes. You'll receive an email and text with driver details prior to pickup."
                }
              },
              {
                "@type": "Question",
                "name": "What is JBSA and what does JBSA Access mean?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "JBSA stands for Joint Base San Antonio, a consolidation of three major military installations: JBSA-Lackland (Air Force), JBSA-Fort Sam Houston (Army/Medical), and JBSA-Randolph (Air Force). 'JBSA Access' means the transportation provider has the specific security clearances and commercial passes required to drive through security gates and onto the base, allowing door-to-door service instead of gate drop-offs."
                }
              },
              {
                "@type": "Question",
                "name": "Why is JBSA Access important for transportation?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Without JBSA Access, Uber, Lyft, and standard taxis can only drop you at the Visitor Control Center or main gate. You must exit with luggage, undergo security screening on foot, then walk or wait for a base shuttle to your destination (which can be miles away). With Rick's JBSA Access, we drive through the checkpoint and drop you directly at your hotel, barracks, home, or office inside the base—a massive convenience factor."
                }
              },
              {
                "@type": "Question",
                "name": "Does Rick's Black Car Service have official access to JBSA military bases in San Antonio?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Rick's Black Car Service is an authorized commercial provider with verified access to all major Joint Base San Antonio (JBSA) installations, including Lackland AFB, Fort Sam Houston, and Randolph AFB. We ensure seamless entry for executive travel and family transportation."
                }
              },
              {
                "@type": "Question",
                "name": "Do your chauffeurs have the necessary credentials for military base entry?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Unlike standard ride-share services, all Rick's Black Car chauffeurs who serve JBSA are fully vetted, background-checked, and hold the required passes (e.g., DBIDS credential or similar) to enter the installation without being sponsored, allowing us to pick you up or drop you off right at your designated location on base."
                }
              },
              {
                "@type": "Question",
                "name": "What information do I need to provide for a pick-up or drop-off on a military base?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "To ensure the quickest entry, we require the following details for all passengers in the vehicle at the time of booking: Full Name, Date of Birth, Primary Form of ID (e.g., CAC, Driver's License, Passport), and Installation Name (e.g., Lackland AFB, BAMC, Randolph AFB)."
                }
              },
              {
                "@type": "Question",
                "name": "Can you provide transportation for Lackland BMT Graduation ceremonies?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we specialize in premium transportation for BMT Graduation families and officials. We monitor all ceremony and guest schedules to ensure you arrive punctually and comfortably right to the designated meeting area, bypassing the hassle of gate entry and parking."
                }
              },
              {
                "@type": "Question",
                "name": "Are there any special rules I need to know when entering a military base in your vehicle?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "While your chauffeur handles the access protocol, all passengers over 16 must be prepared to show a valid government-issued photo ID to Security Forces at the gate upon request. We also ask that you follow the chauffeur's instructions regarding base conduct and vehicle checks, which are standard procedure for all commercial vehicles."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative text-center py-16 px-8 border-b border-border/30 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${faqHero})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/65 to-background/75" />
          <div className="relative z-10">
            <h1 className="text-5xl md:text-6xl font-sans font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Seamless answers for your luxury ride in San Antonio
            </p>
          </div>
        </section>

        {/* FAQ Accordion Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            
            {/* Section 0: General Information */}
            <div className="mb-12">
            <h2 className="text-2xl font-sans text-[#d4af37] text-center mb-6 pb-3 border-b border-border/30">
                General Information
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-gen-1" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    How much does black car service cost in San Antonio?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Our rates start at $75 for airport transfers. Pricing varies based on distance, duration, and service type. Contact us at (210) 296-7409 for a custom quote.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-2" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What's the difference between black car service and Uber/Lyft?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Black car service offers professional chauffeurs, luxury vehicles, guaranteed availability, flat-rate pricing (no surge), flight tracking, and a premium experience. Perfect for business travelers and special occasions where reliability and professionalism matter.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-3" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do you service San Antonio International Airport (SAT)?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Yes! Airport transfers are one of our most popular services. We track your flight and adjust pickup times for delays. Meet and greet service available.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-4" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Are you available 24/7?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Yes, Rick's Black Car Service operates 24 hours a day, 7 days a week, including holidays.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-5" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What type of vehicle do you use?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      We operate a pristine 2025 Audi Q7 with Black Optic & Executive Package - one of Europe's most luxurious SUVs, featuring premium leather, advanced safety features, and spacious comfort.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-6" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    How far in advance should I book?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      We recommend booking at least 24 hours in advance for guaranteed availability, but we often accommodate same-day requests.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-7" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do you offer hourly rates?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Yes, we offer hourly chauffeur service for wine tours, multiple stops, or extended transportation needs.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-8" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Is gratuity included?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Gratuity is not included but always appreciated for exceptional service.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-9" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What is your cancellation policy?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Please contact us at least 24 hours before your scheduled pickup for cancellations or changes.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-gen-10" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do you serve areas outside San Antonio?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      Yes, we serve Boerne, New Braunfels, San Marcos, and can arrange transportation to Austin and other Texas cities.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Section 1: Airport & Booking Logistics */}
            <div className="mb-12">
              <h2 className="text-2xl font-sans text-[#d4af37] text-center mb-6 pb-3 border-b border-border/30">
                Airport & Booking Logistics
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    How does airport pickup at SAT work?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-4 pt-2">
                    <p>
                      <strong>Overview:</strong> We track your flight and time your pickup to your actual landing. <strong>Meeting points:</strong> Terminal A or B, curbside at designated livery zones; meet & greet at baggage claim upon request. <strong>Updates:</strong> Your chauffeur will text you upon arrival.
                    </p>
                    <p>
                      <strong>Flight tracking tools:</strong> We monitor flights using airline data and the Flighty app on iOS for live status. If you provide your incoming and outgoing flight details, we'll deliver real-time updates and proactively notify you of delays, cancellations, gate changes, or other airline issues.
                    </p>
                    <p>
                      <strong>Grace period:</strong> 30 minutes included for domestic arrivals; 60 minutes for international. <strong>Excess time:</strong> Billed in 30-minute increments at the hourly rate for your vehicle class.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What wait time is included?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Included wait time:</strong> 30 minutes for domestic arrivals; 60 minutes for international. <strong>Excess time:</strong> Billed in 30-minute increments at the hourly rate for your black car.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do you offer meet & greet service and what does it cost?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Service:</strong> Chauffeur meets you at baggage claim exit will provide the vehicle make, model, color and license plate number. <strong>Fee:</strong> No Fee.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What is your cancellation policy?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Point-to-point:</strong> Free up to 1 hours before pickup. <strong>Hourly/Charter:</strong> Free up to 24 hours before the scheduled start. <strong>Late cancellations/no-shows:</strong> Full fare applies.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    How do you handle delays and flight changes?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Flight tracking:</strong> We adjust to airline delays automatically. <strong>Schedule changes:</strong> If your pickup time changes significantly, we'll confirm the new time and vehicle availability.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    How far in advance should I book?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Guidance:</strong> As early as possible for airports, weddings, and peak weekends. <strong>Same-day:</strong> Subject to availability; we'll confirm quickly.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Section 2: Policies & Amenities */}
            <div className="mb-12">
              <h2 className="text-2xl font-sans text-[#d4af37] text-center mb-6 pb-3 border-b border-border/30">
                Policies & Amenities
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-7" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do you provide car seats for children?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-4 pt-2">
                    <p>
                      <strong>Car Seat Availability:</strong> Rick's Black Car Service does NOT provide car seats or booster seats. Customers traveling with children who require car seats must bring their own car seats that comply with Texas state law.
                    </p>
                    <p>
                      <strong>Impact on Vehicle Capacity:</strong> Using car seats significantly reduces the available seating capacity in our Audi Q7. While our vehicle can normally accommodate up to 6-7 passengers, car seats take up considerable space and limit how many passengers can travel comfortably and safely.
                    </p>
                    <p>
                      <strong>Important Considerations:</strong> When booking transportation for children requiring car seats, please inform us in advance so we can help plan the appropriate vehicle capacity for your group. Multiple car seats may require booking multiple trips or alternative transportation arrangements to ensure everyone's safety and comfort.
                    </p>
                    <p>
                      <strong>Texas Law:</strong> All children under 8 years old must be secured in a child safety seat system unless they are taller than 4'9". Please ensure your car seat meets Texas safety requirements.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Can I bring a pet?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Policy:</strong> Small pets in carriers are welcome. <strong>Fees:</strong> Cleaning fees may apply. <strong>Service animals:</strong> Always accommodated.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-9" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Is smoking allowed?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Policy:</strong> All vehicles are strictly non-smoking to maintain a clean, elegant cabin.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-10" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What payment methods do you accept?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Methods:</strong> Major credit cards and digital invoices. <strong>Receipts:</strong> E-mailed immediately after the trip.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-11" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What's the difference between hourly service and point-to-point?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Point-to-point:</strong> A-to-B transfers with fixed routing and pricing. <strong>Hourly:</strong> Continuous service with your chauffeur on standby for multiple stops or events.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Section 3: Events & Coverage */}
            <div className="mb-12">
              <h2 className="text-2xl font-sans text-[#d4af37] text-center mb-6 pb-3 border-b border-border/30">
                Events & Coverage
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-12" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What areas do you serve around San Antonio?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Coverage:</strong> Greater San Antonio, including SAT, downtown, Pearl District, River Walk, Alamo Heights, Stone Oak, Dominion, La Cantera, New Braunfels, Boerne, Canyon Lake, Fair Oaks Ranch, Lakehills and Boerne.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-13" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do you handle weddings, proms, and special events?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Events:</strong> Tailored schedules, staging plans, and VIP arrival. <strong>Coordination:</strong> We work with planners and venues to ensure seamless timelines.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-14" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    How do I update or confirm my booking?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Updates:</strong> Reply to your confirmation message or direct text or contact us with your changes. <strong>Confirmations:</strong> You'll receive an email and text with driver details prior to pickup.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Section 4: JBSA & Military Base Access */}
            <div className="mb-12">
              <h2 className="text-2xl font-sans text-[#d4af37] text-center mb-6 pb-3 border-b border-border/30">
                JBSA & Military Base Access
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-mil-0a" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What is JBSA and what does "JBSA Access" mean?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p className="mb-3">
                      <strong>JBSA</strong> stands for <strong>Joint Base San Antonio</strong>, a consolidation of three major military installations:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
                      <li><strong>JBSA-Lackland</strong> (Air Force)</li>
                      <li><strong>JBSA-Fort Sam Houston</strong> (Army/Medical)</li>
                      <li><strong>JBSA-Randolph</strong> (Air Force)</li>
                    </ul>
                    <p>
                      <strong>"JBSA Access"</strong> means the transportation provider (driver and vehicle) has the specific security clearances and commercial passes required to drive through security gates and onto the base, allowing <strong>door-to-door service</strong> instead of gate drop-offs.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-mil-0b" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Why is JBSA Access important for transportation?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p className="mb-3">
                      <strong>Without JBSA Access:</strong> Uber, Lyft, and standard taxis can only drop you at the Visitor Control Center or main gate. You must exit with luggage, undergo security screening on foot, then walk or wait for a base shuttle to your destination—which can be <strong>miles away</strong> inside the base.
                    </p>
                    <p>
                      <strong>With Rick's JBSA Access:</strong> We drive through the checkpoint and drop you <strong>directly at your hotel, barracks, home, or office</strong> inside the base. This door-to-door convenience is especially valuable for families with luggage, medical appointments, or time-sensitive events like BMT graduations.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-mil-1" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Does Rick's Black Car Service have official access to JBSA military bases in San Antonio?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Yes.</strong> Rick's Black Car Service is an authorized commercial provider with <strong>verified access</strong> to all major Joint Base San Antonio (JBSA) installations, including <strong>Lackland AFB</strong>, <strong>Fort Sam Houston</strong>, and <strong>Randolph AFB</strong>. We ensure seamless entry for executive travel and family transportation.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-mil-2" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Do your chauffeurs have the necessary credentials for military base entry?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Absolutely.</strong> Unlike standard ride-share services, all Rick's Black Car chauffeurs who serve JBSA are fully vetted, background-checked, and hold the required passes (e.g., <strong>DBIDS credential</strong> or similar) to enter the installation <strong>without being sponsored</strong>, allowing us to pick you up or drop you off right at your designated location on base.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-mil-3" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    What information do I need to provide for a pick-up or drop-off on a military base?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-4 pt-2">
                    <p>
                      To ensure the quickest entry, we require the following details <strong>for all passengers</strong> in the vehicle at the time of booking:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Full Name</li>
                      <li>Date of Birth</li>
                      <li>Primary Form of ID (e.g., CAC, Driver's License, Passport)</li>
                      <li>Installation Name (e.g., Lackland AFB, BAMC, Randolph AFB)</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-mil-4" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Can you provide transportation for Lackland BMT Graduation ceremonies?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      <strong>Yes, we specialize in premium transportation for BMT Graduation families and officials.</strong> We monitor all ceremony and guest schedules to ensure you arrive punctually and comfortably right to the designated meeting area, bypassing the hassle of gate entry and parking.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-mil-5" className="border-b border-border/30">
                  <AccordionTrigger className="text-left text-lg font-normal text-foreground hover:text-[#d4af37] py-4 transition-colors [&[data-state=open]>svg]:rotate-180">
                    Are there any special rules I need to know when entering a military base in your vehicle?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-4 pt-2">
                    <p>
                      While your chauffeur handles the access protocol, <strong>all passengers over 16 must be prepared to show a valid government-issued photo ID</strong> to Security Forces at the gate upon request. We also ask that you follow the chauffeur's instructions regarding base conduct and vehicle checks, which are standard procedure for all commercial vehicles.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>
      <Footer />
    </div>
  );
};

export default FAQ;
